import { useTheme } from '@emotion/react';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Alert, Box, Button, Drawer, Grid, Header } from '~/components/blocks';
import { useScrollToTop } from '~/hooks/use-scroll-to-top';
import { newDrawerState } from '~/state/settings_practitioners/atoms';

import { PractitionerForm } from '../PractitionerForm';
import { Fields } from '../PractitionerForm/types';
import { useCreatePractitioner } from './use-create_practitioner';

export const NewPractitionerDrawer = () => {
  const theme = useTheme();
  const [containerRef, scrollToTop] = useScrollToTop<HTMLDivElement>();

  const { create, resetError, error, creating } = useCreatePractitioner();
  const [state, setDrawerState] = useAtom(newDrawerState);

  const formRef = useRef<RefAttributeType<typeof PractitionerForm>>(null);
  const initialValues = useMemo<Fields>(
    () => ({
      familyName: '',
      givenName: '',
      phoneticFamilyName: '',
      phoneticGivenName: '',
      username: '',
      password: '',
      passwordConfirmation: '',
      roles: [],
    }),
    [],
  );

  const handleClose = useCallback(() => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    setDrawerState((state) => ({ ...state, isOpen: false }));
  }, [setDrawerState]);

  const handleSend = useCallback(() => {
    if (!formRef.current) return;
    formRef.current.submitForm();
  }, []);
  const handleSubmit = useCallback(
    async (values: Fields) => {
      if (state.organizationId) {
        try {
          await create(values, state.organizationId);
          handleClose();
        } catch {
          scrollToTop();
        }
      }
    },
    [create, handleClose, scrollToTop, state.organizationId],
  );

  useEffect(() => {
    if (state.isOpen) {
      formRef.current?.resetForm();
      resetError();
      scrollToTop();
    }
  }, [resetError, scrollToTop, state.isOpen]);

  return (
    <Drawer open={state.isOpen} onClose={handleClose}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content">
        <Header attached="drawer">スタッフ</Header>
        <Box overflow="auto" mb={theme.space.xl} ref={containerRef}>
          {error && (
            <Alert status="error" m={`0 ${theme.space.xl} ${theme.space.m}`} whiteSpace="pre-wrap">
              {error}
            </Alert>
          )}
          <PractitionerForm
            ref={formRef}
            creatable
            initialValues={initialValues}
            disabled={creating}
            privilege={state.isPrivilege}
            updatedAuth={false}
            onSubmit={handleSubmit}
          />
        </Box>
        <Drawer.Footer>
          <Button wide="fill" use="base" size="l" loading={creating} onClick={handleSend}>
            新規追加
          </Button>
        </Drawer.Footer>
      </Grid>
    </Drawer>
  );
};
